// 3D effects on or off (owner 2026-10-23, after an "Aw, Snap" report from Chrome 109 on an old Windows PC): the
// card turn's lift, the flight shadows and filters, the blur behind windows and of the glass bar, the win
// confetti. Off, the cards still glide and turn (flat): the game always animates (MOTION-001), only the costly
// drawing goes. Options > Settings has a checkbox; untouched ('auto'), a slow device turns them off by itself (an old
// browser by its version for good: oldBrowser(), the checkbox greyed out):
// - a browser too old to be fast here: Chrome before 110 (the last one for Windows 7 / 8.1), Safari before 16 (old
//   iPads and Macs), any browser on an iPhone or iPad before iOS 17 (iOS 16 is the last one of the iPad Pro 12.9 of
//   2015, the iPad 5 and the iPhone 8 / X: a player report 2026-10-25, "the game freezes up all the time", from Chrome
//   on such an iPad, Double Klondike; Chrome there says CriOS, not Chrome/), Firefox before 115 (owner 2026-10-25: the
//   old browsers run the game in the classic mode, the lighter one);
// - little memory or few cores (Chrome's navigator.deviceMemory <= 2 GB, hardwareConcurrency <= 2);
// - frames measured once a few seconds after the start: a median over 30 ms (under ~33 fps), kept in
//   localStorage so the next load starts right.
// Off is `<html data-effects="off">` (app.css, board.css, windows.css, win.css) and `effectsOff()` (flip.ts).
import { setClassicMotion } from '../engine/motion';

export type EffectsPref = 'auto' | 'on' | 'off';

const FRAME_KEY = 'solitaire_storage:perf:frame-ms';
const SLOW_FRAME_MS = 30;

const e2e = (): boolean => typeof window !== 'undefined' && !!(window as { __E2E__?: boolean }).__E2E__;

function storedFrameMs(): number | null {
  try {
    const v = Number(localStorage.getItem(FRAME_KEY));
    return Number.isFinite(v) && v > 0 ? v : null;
  } catch {
    return null;
  }
}

/** A browser too old for the 3D effects, by its version (the list at the top). The classic mode is then fixed: Options
 *  shows its checkbox ticked and greyed out, and a stored "3D on" is not used (owner 2026-10-25: grey it out only for
 *  these; a device that only measured slow keeps the choice, a measurement can be wrong). Under the tests only with
 *  `window.__E2E_OLD_BROWSER__` (Playwright's iPad and iPhone profiles name old iOS versions). */
export function oldBrowser(): boolean {
  if (e2e()) return !!(window as { __E2E_OLD_BROWSER__?: boolean }).__E2E_OLD_BROWSER__;
  return (oldBrowserKnown ??= oldBrowserByAgent());
}
/** Computed once: the top bar asks on every render, and during the auto-complete's burst of renders a Firefox with a
 *  nearly full call stack threw "too much recursion" inside the regexps here (report 2026-10-31, React recovered). */
let oldBrowserKnown: boolean | null = null;
function oldBrowserByAgent(): boolean {
  const ua = navigator.userAgent;
  const chrome = /Chrome\/(\d+)/.exec(ua);
  if (chrome && Number(chrome[1]) < 110) return true;
  const ios = /\bOS (\d+)_\d+(?:_\d+)? like Mac OS X/.exec(ua);
  if (ios && Number(ios[1]) < 17) return true;
  const safari = !chrome && /Version\/(\d+)[.\d]* (?:Mobile\/\S+ )?Safari\//.exec(ua);
  if (safari && Number(safari[1]) < 16) return true;
  const firefox = /Firefox\/(\d+)/.exec(ua);
  return !!firefox && Number(firefox[1]) < 115;
}

/** The device looks slow from what the browser tells, or from the last frame measurement. */
export function looksSlow(): boolean {
  if (e2e()) return oldBrowser(); // the tests decide the setting themselves (and a busy test machine is not a slow PC)
  if (oldBrowser()) return true;
  const memory = (navigator as { deviceMemory?: number }).deviceMemory;
  if (memory !== undefined && memory <= 2) return true;
  if (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 2) return true;
  const ms = storedFrameMs();
  return ms !== null && ms > SLOW_FRAME_MS;
}

export const effectsOnFor = (pref: EffectsPref): boolean =>
  oldBrowser() ? false : pref === 'on' ? true : pref === 'off' ? false : !looksSlow();

export function applyEffects(on: boolean): void {
  if (on) delete document.documentElement.dataset.effects;
  else document.documentElement.dataset.effects = 'off';
  // off is the classic mode (owner 2026-10-23, Options "Tryb klasyczny"): the legacy motion too (engine/motion.ts)
  setClassicMotion(!on);
}

export const effectsOff = (): boolean => document.documentElement.dataset.effects === 'off';

/** Measures 120 frames once (the page visible), stores the median and calls `done` with it. */
export function measureFrames(done: (medianMs: number) => void): void {
  if (e2e()) return;
  const frames: number[] = [];
  let last = 0;
  const tick = (t: number) => {
    if (document.hidden) {
      last = 0;
      requestAnimationFrame(tick);
      return;
    }
    if (last) frames.push(t - last);
    last = t;
    if (frames.length < 120) {
      requestAnimationFrame(tick);
      return;
    }
    frames.sort((a, b) => a - b);
    const median = frames[60]!;
    try {
      localStorage.setItem(FRAME_KEY, String(Math.round(median * 10) / 10));
    } catch {
      /* no storage: measured again next time */
    }
    done(median);
  };
  requestAnimationFrame(tick);
}
